import { useState, useEffect, useCallback } from 'react'

/**
 * Custom hook for data fetching with loading and error states
 * Provides a clean interface for API calls that can be easily swapped to real backend
 * 
 * @param {Function} fetchFn - Async function that returns data
 * @param {Array} deps - Dependency array for re-fetching
 */
export function useDataFetch(fetchFn, deps = []) {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const fetch = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const result = await fetchFn()
            setData(result)
        } catch (err) {
            setError(err.message || 'An error occurred')
            console.error('Data fetch error:', err)
        } finally {
            setLoading(false)
        }
    }, [fetchFn])

    useEffect(() => {
        fetch()
    }, deps) // eslint-disable-line react-hooks/exhaustive-deps

    return {
        data,
        loading,
        error,
        refetch: fetch,
    }
}

/**
 * Hook for mutations (create, update, delete)
 * Returns a function to execute the mutation with loading/error handling
 */
export function useMutation(mutationFn) {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const mutate = useCallback(async (...args) => {
        setLoading(true)
        setError(null)
        try {
            const result = await mutationFn(...args)
            return result
        } catch (err) {
            setError(err.message || 'An error occurred')
            throw err
        } finally {
            setLoading(false)
        }
    }, [mutationFn])

    return {
        mutate,
        loading,
        error,
    }
}
